import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { OrganizationQuery } from '@core/organization/organization.query';
import { OrganizationContextService } from '@core/context/organization-context.service';
import { investorRoute, selectCompanyAbsoluteRoute } from '@shared/shared-routes';
import { Observable, combineLatest } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';

import { AuthorizationService } from './authorization.service';

@Injectable({
  providedIn: 'root',
})
export class IsEmittentGuard {
  constructor(
    private router: Router,
    private organizationQuery: OrganizationQuery,
    private authorization: AuthorizationService,
    private organizationContextService: OrganizationContextService,
  ) {}

  public canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    // Check query params from the target route (available during navigation)
    const queryParams = route.queryParams || {};
    const hasFundDdqRunsContext = queryParams['context'] === 'fund-ddq-runs';

    // If we're navigating to a route with fund-ddq-runs context, set it immediately
    // Extract issuerUid from query params (set by create dialog) or preserve existing context
    if (hasFundDdqRunsContext) {
      const existingContext = this.organizationContextService.getContextValue();
      this.organizationContextService.setContext({
        context: 'fund-ddq-runs',
        viewAs: (queryParams['viewAs'] as 'investor' | 'emittent' | 'client') || 'investor',
        serviceType: 'emittent',
        // Prioritize issuerUid from query params, fallback to existing context
        issuerUid: queryParams['issuerUid'] || existingContext?.issuerUid,
      });
    }

    return combineLatest([
      this.organizationQuery.selectChosenOrganizationType(),
      this.organizationContextService.getEffectiveOrganizationType(),
    ]).pipe(
      map(([actualType, effectiveType]) => {
        // Allow access if:
        // 1. Actual organization type is emittent, OR
        // 2. Effective type is emittent (in fund-ddq-runs context - service calls use emittent URLs)
        //    This allows investors to access product routes when viewing emittent products
        if (this.authorization.isEmittentCompany(actualType) || this.authorization.isEmittentCompany(effectiveType)) {
          return true;
        }
        // Fallback: redirect to investor route or company selection
        return this.router.parseUrl(investorRoute) || this.router.parseUrl(selectCompanyAbsoluteRoute);
      }),
      first(),
    );
  }

  public canLoad(): Observable<boolean> | Promise<boolean> | boolean {
    return this.organizationQuery.selectChosenOrganizationType().pipe(
      filter((type) => type !== null),
      map((type) => {
        return this.authorization.isEmittentCompany(type);
      }),
      first(),
    );
  }
}
